import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Snackbar,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn,
  School,
  Interests,
  Groups,
  Person,
  PersonAdd,
  FilterList,
} from '@mui/icons-material';
import {
  apiAcceptFriendRequest,
  apiListFriendRequests,
  apiListFriends,
  apiSearchUsers,
  apiSendFriendRequest,
  type UserCard,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { initials } from '../../utils/strings';

type SearchUser = UserCard & {
  hometown?: string | null;
  bio?: string | null;
  interests?: string[];
  clubs?: string[];
};

const majors = [
  'Computer Science',
  'Engineering',
  'Business',
  'Arts & Sciences',
  'Nursing',
  'Public Health',
  'Architecture',
  'Design',
  'Information Systems',
  'Other',
];

const interests = [
  'Programming',
  'AI',
  'Music',
  'Gaming',
  'Engineering',
  'Robotics',
  'Sports',
  'Photography',
  'Business',
  'Marketing',
  'Travel',
  'Cooking',
  'Psychology',
  'Art',
  'Dance',
];

const Search: React.FC = () => {
  const { user: me } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<SearchUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [relationsLoading, setRelationsLoading] = useState(false);
  const [friendIds, setFriendIds] = useState<Set<string>>(new Set());
  const [outgoingIds, setOutgoingIds] = useState<Set<string>>(new Set());
  const [incomingByUserId, setIncomingByUserId] = useState<Record<string, string>>({});
  const [requestingUserId, setRequestingUserId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [filters, setFilters] = useState({
    major: '',
    year: '',
    hometown: '',
    interests: [] as string[],
  });
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  const openUserProfile = (userId?: string | null) => {
    if (!userId) return;
    const target = me?.id && userId === me.id ? '/profile' : `/profile/${userId}`;
    navigate(target);
  };

  const loadRelationshipState = async (signal?: AbortSignal) => {
    try {
      setRelationsLoading(true);
      const [friendsResp, outgoingResp, incomingResp] = await Promise.all([
        apiListFriends(undefined, undefined, 200, signal),
        apiListFriendRequests('outgoing', undefined, 200, signal),
        apiListFriendRequests('incoming', undefined, 200, signal),
      ]);

      setFriendIds(new Set((friendsResp.items || []).map((f: any) => f.id)));
      setOutgoingIds(new Set((outgoingResp.items || []).map((r: any) => r.to?.id).filter(Boolean)));

      const incomingMap: Record<string, string> = {};
      for (const req of incomingResp.items || []) {
        if (req?.from?.id && req?.id) incomingMap[req.from.id] = req.id;
      }
      setIncomingByUserId(incomingMap);
    } catch (error) {
      if ((error as any)?.name === 'AbortError') return;
      console.error('Failed to load relationship state', error);
    } finally {
      if (!signal?.aborted) setRelationsLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        setLoading(true);
        const response = await apiSearchUsers(undefined, undefined, 200, controller.signal);
        setUsers(response.items as SearchUser[]);
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('Failed to fetch search users', error);
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadRelationshipState(controller.signal);
    return () => controller.abort();
  }, []);

  const filteredUsers = users.filter(user => {
    const displayName = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    const major = user.major || '';
    const hometown = user.hometown || '';
    const userInterests = user.interests || [];

    const matchesSearch =
      displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      major.toLowerCase().includes(searchTerm.toLowerCase()) ||
      hometown.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userInterests.some((interest) => interest.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesMajor = !filters.major || major === filters.major;
    const matchesYear = !filters.year || String(user.year ?? '') === filters.year;
    const matchesHometown = !filters.hometown || hometown.toLowerCase().includes(filters.hometown.toLowerCase());
    const matchesInterests =
      filters.interests.length === 0 || filters.interests.some((interest) => userInterests.includes(interest));

    return matchesSearch && matchesMajor && matchesYear && matchesHometown && matchesInterests;
  });

  const handleInterestToggle = (interest: string) => {
    setFilters(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const clearFilters = () => {
    setFilters({
      major: '',
      year: '',
      hometown: '',
      interests: [],
    });
  };

  const sendRequest = async (toUserId: string) => {
    try {
      setRequestingUserId(toUserId);
      await apiSendFriendRequest(toUserId);
      setOutgoingIds((prev) => {
        const next = new Set(prev);
        next.add(toUserId);
        return next;
      });
      setNotice({ severity: 'success', message: 'Friend request sent.' });
    } catch (error: any) {
      const message = String(error?.message || 'Failed to send friend request');
      if (message.includes('already_friends')) {
        setFriendIds((prev) => {
          const next = new Set(prev);
          next.add(toUserId);
          return next;
        });
        setOutgoingIds((prev) => {
          const next = new Set(prev);
          next.delete(toUserId);
          return next;
        });
        setNotice({ severity: 'info', message: 'You are already friends.' });
      } else if (message.includes('already_pending')) {
        setOutgoingIds((prev) => {
          const next = new Set(prev);
          next.add(toUserId);
          return next;
        });
        setNotice({ severity: 'info', message: 'Request is already pending.' });
      } else {
        setNotice({ severity: 'error', message: 'Could not send request. Try again.' });
      }
    } finally {
      setRequestingUserId(null);
    }
  };

  const acceptIncomingRequest = async (fromUserId: string) => {
    const requestId = incomingByUserId[fromUserId];
    if (!requestId) return;

    try {
      setRequestingUserId(fromUserId);
      await apiAcceptFriendRequest(requestId);
      setFriendIds((prev) => {
        const next = new Set(prev);
        next.add(fromUserId);
        return next;
      });
      setIncomingByUserId((prev) => {
        const next = { ...prev };
        delete next[fromUserId];
        return next;
      });
      setNotice({ severity: 'success', message: 'Friend request accepted.' });
    } catch {
      setNotice({ severity: 'error', message: 'Could not accept request. Try again.' });
    } finally {
      setRequestingUserId(null);
    }
  };

  const getFriendAction = (userId: string) => {
    if (me?.id && userId === me.id) return { label: 'You', disabled: true, onClick: undefined as (() => void) | undefined };
    if (friendIds.has(userId)) return { label: 'Friends', disabled: true, onClick: undefined as (() => void) | undefined };
    if (incomingByUserId[userId]) {
      return {
        label: 'Accept Request',
        disabled: requestingUserId === userId,
        onClick: () => void acceptIncomingRequest(userId),
      };
    }
    if (outgoingIds.has(userId)) return { label: 'Request Sent', disabled: true, onClick: undefined as (() => void) | undefined };
    return {
      label: 'Send Friend Request',
      disabled: requestingUserId === userId,
      onClick: () => void sendRequest(userId),
    };
  };

  const showSearchSkeleton = loading || (relationsLoading && users.length === 0);

  return (
    <Box sx={{ width: '100%', maxWidth: 1040, mx: 'auto' }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800, letterSpacing: '-0.02em' }}
      >
        <Person sx={{ color: 'primary.main' }} />
        Find Students
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Connect with fellow students by searching for people with similar interests, majors, or backgrounds.
      </Typography>

      {/* Search Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by name, major, hometown, or interests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          startIcon={<FilterList />}
          onClick={() => setShowFilters(!showFilters)}
        >
          Filters
        </Button>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Filters</Typography>
              <Button size="small" onClick={clearFilters}>
                Clear All
              </Button>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Major</InputLabel>
                  <Select
                    value={filters.major}
                    label="Major"
                    onChange={(e) => setFilters({ ...filters, major: e.target.value })}
                  >
                    <MenuItem value="">All Majors</MenuItem>
                    {majors.map((major) => (
                      <MenuItem key={major} value={major}>
                        {major}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={filters.year}
                    label="Year"
                    onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                  >
                    <MenuItem value="">All Years</MenuItem>
                    <MenuItem value="1">1st Year</MenuItem>
                    <MenuItem value="2">2nd Year</MenuItem>
                    <MenuItem value="3">3rd Year</MenuItem>
                    <MenuItem value="4">4th Year</MenuItem>
                    <MenuItem value="5">5th Year</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Hometown"
                  value={filters.hometown}
                  onChange={(e) => setFilters({ ...filters, hometown: e.target.value })}
                  placeholder="City, State"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    {filteredUsers.length} students found
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Interests</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {interests.map((interest) => (
                    <Chip
                      key={interest}
                      label={interest}
                      size="small"
                      variant={filters.interests.includes(interest) ? "filled" : "outlined"}
                      color={filters.interests.includes(interest) ? "primary" : "default"}
                      onClick={() => handleInterestToggle(interest)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {showSearchSkeleton ? (
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid item xs={12} md={6} key={`search-skeleton-${index}`}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={60} height={60} animation="wave" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="58%" height={30} animation="wave" />
                      <Skeleton variant="text" width="62%" height={20} animation="wave" />
                      <Skeleton variant="text" width="50%" height={20} animation="wave" />
                    </Box>
                  </Box>

                  <Skeleton variant="text" width="95%" height={22} animation="wave" />
                  <Skeleton variant="text" width="78%" height={22} animation="wave" sx={{ mb: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Skeleton variant="text" width={110} height={20} animation="wave" sx={{ mb: 1 }} />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Skeleton variant="rounded" width={84} height={24} animation="wave" />
                      <Skeleton variant="rounded" width={74} height={24} animation="wave" />
                      <Skeleton variant="rounded" width={88} height={24} animation="wave" />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Skeleton variant="rounded" width={100} height={32} animation="wave" />
                    <Skeleton variant="rounded" width={90} height={32} animation="wave" />
                    <Skeleton variant="rounded" width={128} height={32} animation="wave" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3}>
          {filteredUsers.map((user) => {
          const displayName = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Student';
          const friendAction = getFriendAction(user.id);
          return (
          <Grid item xs={12} md={6} key={user.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Avatar
                    src={(user as any).avatarUrl || undefined}
                    sx={{
                      width: 60,
                      height: 60,
                      background: 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                    }}
                    onClick={() => openUserProfile(user.id)}
                  >
                    {initials(displayName)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      gutterBottom
                      sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => openUserProfile(user.id)}
                    >
                      {displayName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <School fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {user.major || 'Undeclared'}{user.year ? ` • Year ${user.year}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {user.hometown || 'Location not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {user.bio && (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {user.bio}
                  </Typography>
                )}

                {(user.interests || []).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Interests fontSize="small" />
                      Interests
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {(user.interests || []).map((interest) => (
                        <Chip
                          key={interest}
                          label={interest}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {(user.clubs || []).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Groups fontSize="small" />
                      Clubs & Organizations
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {(user.clubs || []).map((club) => (
                        <Chip
                          key={club}
                          label={club}
                          size="small"
                          variant="outlined"
                          color="secondary"
                          clickable
                          onClick={() => navigate(`/clubs?club=${encodeURIComponent(club)}`)}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Person />}
                    onClick={() => openUserProfile(user.id)}
                  >
                    View Profile
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/messages/${user.id}`)}
                    disabled={!user.id}
                  >
                    Message
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonAdd />}
                    onClick={friendAction.onClick}
                    disabled={friendAction.disabled}
                  >
                    {friendAction.label}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          );
          })}
        </Grid>
      )}

      {!showSearchSkeleton && filteredUsers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No students found matching your criteria
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting your search terms or filters
          </Typography>
        </Box>
      )}

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={3000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80 } }}
      >
        {notice ? <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert> : <Box />}
      </Snackbar>
    </Box>
  );
};

export default Search; 